import {
  assumeRelativeParts,
  machineTelemetryReleaseKey,
  operatorTelemetryReleaseKey,
  readReleaseEntry,
} from "./dashboardActiveTelemetry";

export const FIELD_ALERT_POPUP_MS = 7000;
export const FIELD_ALERT_DURATION_MIN = 2;
export const FIELD_ALERT_DURATION_MAX = 10;
export const FIELD_ALERT_VOLUME_MIN = 1;
export const FIELD_ALERT_VOLUME_MAX = 10;
export const FIELD_ALERT_PREFS_KEY = "dashboard-field-alert-prefs";

export const FIELD_ALERT_SOUNDS = [
  { id: "classic", label: "Bip clássico" },
  { id: "soft", label: "Bip suave" },
  { id: "double", label: "Dois bips" },
  { id: "chime", label: "Sino" },
  { id: "urgent", label: "Urgente" },
];

export const FIELD_ALERT_TYPES = [
  { id: "telemetryStart", label: "Iniciou telemetria" },
  { id: "assumeMachine", label: "Assumiu máquina" },
  { id: "intervalPause", label: "Pausa por intervalo" },
  { id: "resumePause", label: "Retornou da pausa" },
  { id: "finishMachine", label: "Finalizou máquina" },
  { id: "telemetryEnd", label: "Finalizou telemetria" },
  { id: "radio", label: "Comunicação de rádio" },
];

export function clampFieldAlertDuration(value) {
  const seconds = Math.round(Number(value));
  if (!Number.isFinite(seconds)) return 7;
  return Math.min(
    FIELD_ALERT_DURATION_MAX,
    Math.max(FIELD_ALERT_DURATION_MIN, seconds)
  );
}

export function fieldAlertDurationMs(prefs) {
  return clampFieldAlertDuration(prefs && prefs.durationSeconds) * 1000;
}

export function clampFieldAlertVolume(value) {
  const level = Math.round(Number(value));
  if (!Number.isFinite(level)) return 6;
  return Math.min(
    FIELD_ALERT_VOLUME_MAX,
    Math.max(FIELD_ALERT_VOLUME_MIN, level)
  );
}

export function resolveFieldAlertSound(soundId) {
  return FIELD_ALERT_SOUNDS.some((item) => item.id === soundId)
    ? soundId
    : "classic";
}

export function defaultFieldAlertPrefs() {
  return FIELD_ALERT_TYPES.reduce(
    (acc, item) => {
      acc[item.id] = true;
      return acc;
    },
    {
      durationSeconds: 7,
      soundEnabled: true,
      soundId: "classic",
      volume: 6,
      desktopNotify: false,
    }
  );
}

export function loadFieldAlertPrefs() {
  const defaults = defaultFieldAlertPrefs();
  try {
    const raw = window.localStorage.getItem(FIELD_ALERT_PREFS_KEY);
    if (!raw) return defaults;
    const saved = JSON.parse(raw);
    const prefs = FIELD_ALERT_TYPES.reduce((acc, item) => {
      acc[item.id] = saved[item.id] !== false;
      return acc;
    }, {});
    prefs.durationSeconds = clampFieldAlertDuration(
      saved.durationSeconds == null ? defaults.durationSeconds : saved.durationSeconds
    );
    prefs.soundEnabled = saved.soundEnabled !== false;
    prefs.soundId = resolveFieldAlertSound(saved.soundId);
    prefs.volume = clampFieldAlertVolume(
      saved.volume == null ? defaults.volume : saved.volume
    );
    prefs.desktopNotify = saved.desktopNotify === true;
    return prefs;
  } catch (error) {
    return defaults;
  }
}

function playTone(ctx, { type, frequency, start, peak, stop }) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = type || "sine";
  oscillator.frequency.setValueAtTime(frequency, start);
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, stop);
  oscillator.start(start);
  oscillator.stop(stop);
}

export function playFieldAlertBeep(options = {}) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return;
  const soundId = resolveFieldAlertSound(options.soundId);
  const volume = clampFieldAlertVolume(options.volume);
  const peak = 0.03 + (volume / 10) * 0.22;
  try {
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    if (soundId === "soft") {
      playTone(ctx, { frequency: 660, start: now, peak, stop: now + 0.36 });
    } else if (soundId === "double") {
      playTone(ctx, { frequency: 880, start: now, peak, stop: now + 0.12 });
      playTone(ctx, {
        frequency: 880,
        start: now + 0.18,
        peak,
        stop: now + 0.3,
      });
    } else if (soundId === "chime") {
      playTone(ctx, { frequency: 523, start: now, peak, stop: now + 0.18 });
      playTone(ctx, {
        frequency: 784,
        start: now + 0.14,
        peak,
        stop: now + 0.4,
      });
    } else if (soundId === "urgent") {
      [0, 0.14, 0.28].forEach((offset) => {
        playTone(ctx, {
          type: "square",
          frequency: 1240,
          start: now + offset,
          peak: peak * 0.7,
          stop: now + offset + 0.1,
        });
      });
    } else {
      playTone(ctx, { frequency: 880, start: now, peak, stop: now + 0.14 });
      playTone(ctx, {
        frequency: 1175,
        start: now + 0.1,
        peak,
        stop: now + 0.3,
      });
    }
    window.setTimeout(() => {
      ctx.close();
    }, 700);
  } catch (error) {
    // ignore autoplay / unsupported audio
  }
}

export function saveFieldAlertPrefs(prefs) {
  try {
    window.localStorage.setItem(FIELD_ALERT_PREFS_KEY, JSON.stringify(prefs));
  } catch (error) {
    // ignore quota / private mode
  }
}

export function enabledFieldAlertCount(prefs) {
  return FIELD_ALERT_TYPES.filter((item) => prefs && prefs[item.id]).length;
}

function sessionsByOperator(sessions) {
  const map = new Map();
  (sessions || []).forEach((session) => {
    if (!session || session.operatorId == null || session.operatorId === "") {
      return;
    }
    map.set(session.operatorId, session);
  });
  return map;
}

function releaseForOperator(releases, operatorId) {
  return readReleaseEntry(
    (releases || {})[operatorTelemetryReleaseKey(operatorId)]
  );
}

function releaseAtFor(releases, operatorId, machineIdent) {
  const machine = readReleaseEntry(
    (releases || {})[machineTelemetryReleaseKey(machineIdent)]
  );
  if (machine && machine.at) return machine.at;
  const operator = releaseForOperator(releases, operatorId);
  return (operator && operator.at) || null;
}

/** Hora do dia (HH:mm) no fuso local do navegador. */
export function fieldAlertTimeOfDay(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/** Duração entre início e fim — ex.: "12 min", "2 h 15 min". */
export function fieldAlertDurationLabel(startedAt, endedAt) {
  const start = new Date(startedAt);
  const end = new Date(endedAt || Date.now());
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const minutes = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 60000));
  if (minutes < 1) return "menos de 1 min";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) {
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const dayHours = hours % 24;
  if (dayHours && rest) return `${days} d ${dayHours} h ${rest} min`;
  if (dayHours) return `${days} d ${dayHours} h`;
  return `${days} d`;
}

/** Sempre: quanto tempo atrás o evento aconteceu (sync atrasado / abriu a tela depois). */
export function fieldAlertRelativeLabel(iso, t, now = new Date()) {
  if (!iso) return "";
  const parts = assumeRelativeParts(iso, now);
  if (parts.unit === "now") return t("agora");
  if (parts.unit === "minutes") return t("há {{count}} min", { count: parts.count });
  if (parts.unit === "hours") return t("há {{count}} h", { count: parts.count });
  return t("há {{count}} d", { count: parts.count });
}

function withEventTimes(event, { at, startedAt } = {}) {
  const next = { ...event };
  if (at) next.at = at;
  if (startedAt) next.startedAt = startedAt;
  return next;
}

export function detectFieldAlertEvents({
  previousSessions,
  nextSessions,
  previousReleases,
  nextReleases,
  primed = true,
} = {}) {
  if (!primed) return [];

  const prev = sessionsByOperator(previousSessions);
  const next = sessionsByOperator(nextSessions);
  const ids = new Set([...prev.keys(), ...next.keys()]);
  const events = [];

  ids.forEach((operatorId) => {
    const before = prev.get(operatorId);
    const after = next.get(operatorId);
    const name =
      (after && after.operatorName) ||
      (before && before.operatorName) ||
      "";
    const prevRelease = releaseForOperator(previousReleases, operatorId);
    const nextRelease = releaseForOperator(nextReleases, operatorId);
    const wasPaused = prevRelease && prevRelease.reason === "pause";
    const isPaused = nextRelease && nextRelease.reason === "pause";

    if (!before && after) {
      const at = after.assumedAt || null;
      if (wasPaused) {
        events.push(
          withEventTimes(
            {
              type: "resumePause",
              operatorName: name,
              machineIdent: after.machineIdent || "",
            },
            { at, startedAt: at }
          )
        );
      } else {
        events.push(
          withEventTimes(
            {
              type: "telemetryStart",
              operatorName: name,
              machineIdent: after.machineIdent || "",
            },
            { at, startedAt: at }
          )
        );
      }
      if (after.machineIdent) {
        events.push(
          withEventTimes(
            {
              type: "assumeMachine",
              operatorName: name,
              machineIdent: after.machineIdent,
            },
            { at, startedAt: at }
          )
        );
      }
      return;
    }

    if (before && after) {
      if (!before.machineIdent && after.machineIdent) {
        const at = after.assumedAt || null;
        events.push(
          withEventTimes(
            {
              type: "assumeMachine",
              operatorName: name,
              machineIdent: after.machineIdent,
            },
            { at, startedAt: at }
          )
        );
      }
      if (before.machineIdent && !after.machineIdent) {
        const at = releaseAtFor(nextReleases, operatorId, before.machineIdent);
        events.push(
          withEventTimes(
            {
              type: "finishMachine",
              operatorName: name,
              machineIdent: before.machineIdent,
            },
            { at, startedAt: before.assumedAt || null }
          )
        );
      }
      return;
    }

    if (before && !after) {
      const at = releaseAtFor(
        nextReleases,
        operatorId,
        before.machineIdent || before.lastMachineIdent
      );
      const startedAt = before.assumedAt || null;
      if (isPaused) {
        events.push(
          withEventTimes(
            {
              type: "intervalPause",
              operatorName: name,
              machineIdent: before.machineIdent || "",
            },
            { at, startedAt }
          )
        );
        return;
      }
      if (before.machineIdent) {
        events.push(
          withEventTimes(
            {
              type: "finishMachine",
              operatorName: name,
              machineIdent: before.machineIdent,
            },
            { at, startedAt }
          )
        );
      }
      events.push(
        withEventTimes(
          {
            type: "telemetryEnd",
            operatorName: name,
            machineIdent: before.machineIdent || "",
          },
          { at, startedAt }
        )
      );
    }
  });

  return events;
}

export function shouldShowFieldAlertDesktop(prefs, hasFocus) {
  return Boolean(prefs && prefs.desktopNotify) && !hasFocus;
}

export function requestFieldAlertDesktopPermission() {
  if (typeof Notification === "undefined") return Promise.resolve("unsupported");
  if (Notification.permission === "granted") return Promise.resolve("granted");
  if (Notification.permission === "denied") return Promise.resolve("denied");
  return Notification.requestPermission();
}

export function showFieldAlertDesktopNotification(event, t) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return null;
  }
  const title = fieldAlertTitle(event && event.type, t);
  const body = fieldAlertMessage(event, t);
  try {
    const notification = new Notification(title, {
      body,
      icon: "/favicon.ico",
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    window.setTimeout(() => notification.close(), fieldAlertDurationMs({ durationSeconds: 8 }));
    return notification;
  } catch (error) {
    return null;
  }
}

export function filterEnabledFieldAlerts(events, prefs) {
  return (events || []).filter((event) => prefs && prefs[event.type]);
}

export function fieldAlertTitle(type, t) {
  const item = FIELD_ALERT_TYPES.find((entry) => entry.id === type);
  return t(item ? item.label : "Notificação");
}

function fieldAlertShowsDuration(type) {
  return (
    type === "finishMachine" ||
    type === "telemetryEnd" ||
    type === "intervalPause"
  );
}

export function fieldAlertMessage(event, t, now = new Date()) {
  const name = (event && event.operatorName) || t("Operador");
  const machine = event && event.machineIdent;
  let base = t("Notificação");
  switch (event && event.type) {
    case "telemetryStart":
      base = t("{{name}} iniciou a telemetria", { name });
      break;
    case "assumeMachine":
      base = machine
        ? t("{{name}} assumiu a máquina {{machine}}", { name, machine })
        : t("{{name}} assumiu a máquina", { name });
      break;
    case "intervalPause":
      base = t("{{name}} deu pausa nos trabalhos", { name });
      break;
    case "resumePause":
      base = t("{{name}} retornou da pausa", { name });
      break;
    case "finishMachine":
      base = machine
        ? t("{{name}} finalizou a máquina {{machine}}", { name, machine })
        : t("{{name}} finalizou a máquina", { name });
      break;
    case "telemetryEnd":
      base = t("{{name}} finalizou a telemetria", { name });
      break;
    case "radio":
      return t("Nova comunicação de rádio");
    default:
      return base;
  }

  const time = fieldAlertTimeOfDay(event && event.at);
  if (time) {
    base = t("{{message}} às {{time}}", { message: base, time });
  }

  if (
    fieldAlertShowsDuration(event && event.type) &&
    event &&
    event.startedAt &&
    event.at
  ) {
    const duration = fieldAlertDurationLabel(event.startedAt, event.at);
    if (duration) {
      base = t("{{message}} · ficou {{duration}}", {
        message: base,
        duration,
      });
    }
  }

  // Relativo sempre que houver horário: cobre atraso de internet e painel aberto depois.
  const relative = fieldAlertRelativeLabel(event && event.at, t, now);
  if (relative) {
    base = t("{{message}} · {{relative}}", { message: base, relative });
  }

  return base;
}
